import React, { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";

const benchmarkFeatures = [
  {
    title: "Account flows",
    detail: "Signup, login, logout, duplicate email, password validation, and role-aware session checks.",
  },
  {
    title: "Product discovery",
    detail: "Category pages, product details, flash-sale navigation, search, sorting, and filter validations.",
  },
  {
    title: "Cart to checkout",
    detail: "Quantity updates, totals, address selection, payment simulation, invoice verification, and order history.",
  },
  {
    title: "API readiness",
    detail: "Status codes, payload validation, auth checks, data consistency, and negative response scenarios.",
  },
];

const practiceFlows = [
  {
    id: "auth",
    label: "Auth Suite",
    difficulty: "Intermediate",
    objective: "Validate the full authentication lifecycle like a real ecommerce app.",
    problemStatement:
      "Users report that account creation works on some builds, but login state and duplicate-email validation behave inconsistently after release.",
    paths: [
      { label: "Home", to: "/home", note: "Landing page visibility and hero assertions" },
      { label: "Register", to: "/register", note: "Create account and validation path" },
      { label: "Login", to: "/login", note: "Positive and negative authentication flow" },
    ],
    testCases: [
      "Verify a new user can register with valid mandatory details and sees a clear success state.",
      "Verify duplicate email registration is blocked with a stable validation message.",
      "Verify login with valid credentials lands on the correct dashboard or post-login state.",
      "Verify login with invalid credentials shows a secure and non-technical error message.",
      "Verify logout clears the active session and user-specific menu links.",
    ],
    assertions: [
      "Token or session state should update exactly once after successful login.",
      "Validation errors should remain field-specific and not break layout.",
      "Logged-in header text or dashboard redirect should match the signed-in user state.",
    ],
    pitfalls: [
      "Relying only on URL change instead of verifying authenticated UI state.",
      "Ignoring duplicate email and invalid password negative coverage.",
      "Hardcoding waits instead of waiting for validation or redirect state.",
    ],
  },
  {
    id: "catalog",
    label: "Catalog Suite",
    difficulty: "Beginner to Intermediate",
    objective: "Practice storefront coverage similar to ecommerce automation websites, but with richer QA context.",
    problemStatement:
      "The business team wants confidence that product cards, category browsing, flash sale tiles, and product details remain stable after data refreshes.",
    paths: [
      { label: "Storefront", to: "/product", note: "Product card rendering and category entry point" },
      { label: "Flash Sales", to: "/flash-sales", note: "High-discount navigation and visibility checks" },
      { label: "Phones & Tablets", to: "/product_phone_tablets", note: "Category listing validation" },
      { label: "Product Details", to: "/product-details/1", note: "Image, title, price, specs, and navigation" },
    ],
    testCases: [
      "Verify all key product cards render with image, title, price, and action link.",
      "Verify flash sale cards navigate to the correct product detail page.",
      "Verify category listings support repeatable sorting, filtering, and pagination-style browsing behavior.",
      "Verify product detail pages show stable content for image, title, price, and major product information.",
      "Verify browse-only catalog pages do not expose broken checkout controls or dead-end ecommerce actions.",
    ],
    assertions: [
      "Product count and first visible card should remain stable after filter changes.",
      "Product detail data should match the selected card source data.",
      "Broken image placeholders should never replace valid product images in normal flow.",
    ],
    pitfalls: [
      "Using brittle nth-child locators for product cards.",
      "Asserting only click action without validating destination data.",
      "Skipping data-driven checks after filter or sort interactions.",
    ],
  },
  {
    id: "checkout",
    label: "Checkout Suite",
    difficulty: "Advanced",
    objective: "Simulate a real revenue-critical workflow with address, payment, invoice, and order-history assertions.",
    problemStatement:
      "A production defect was reported: payment succeeds, but students sometimes do not see invoice or order confirmation. Build an automation suite that can catch it early.",
    paths: [
      { label: "Catalog", to: "/product", note: "Select product from listing" },
      { label: "Product Detail", to: "/product-details/1", note: "Validate quantity and product state" },
      { label: "Order History", to: "/order-history", note: "Verify post-order persistence" },
    ],
    testCases: [
      "Verify a user can add product to cart and quantity changes reflect in summary values.",
      "Verify address selection or creation state is correctly persisted before payment.",
      "Verify payment success creates one order and one invoice, not duplicate entries.",
      "Verify order history displays the latest completed order with correct product and amount.",
      "Verify payment failure or interruption does not create a false success order.",
    ],
    assertions: [
      "Grand total should equal quantity multiplied by unit price plus any applied fee rules.",
      "Invoice identifier should be unique and linked to the correct order record.",
      "Order history should show exactly one new order after one successful payment.",
    ],
    pitfalls: [
      "Validating only success banner and not persistence in order history.",
      "Ignoring duplicate submission by rapid repeated clicks on pay button.",
      "Skipping network or database-backed state assertions for invoice creation.",
    ],
  },
  {
    id: "api",
    label: "API + Edge Cases",
    difficulty: "Advanced",
    objective: "Extend UI automation thinking into API contracts, authorization, and data integrity.",
    problemStatement:
      "The frontend looks correct, but API regressions cause inconsistent product data, stale cart totals, and unauthorized access exposure. Students should test beyond the UI.",
    paths: [
      { label: "API Testing Guide", to: "/api-testing", note: "Request and response validation" },
      { label: "API Cheat Sheet", to: "/API-testing-cheatSheet", note: "Quick revision for codes and payloads" },
      { label: "Bug Tracker", to: "/bugs", note: "Raise defect with RCA and evidence" },
    ],
    testCases: [
      "Verify product API returns stable schema and non-empty required fields.",
      "Verify unauthorized user cannot access protected order or invoice APIs.",
      "Verify cart summary API matches the UI totals shown to the user.",
      "Verify invalid input returns correct status code and meaningful error body.",
      "Verify duplicate payment callback does not create duplicate order records.",
    ],
    assertions: [
      "Response schema should match contract for id, title, price, availability, and category fields.",
      "Protected endpoints should return 401 or 403 for invalid or missing auth.",
      "Negative API responses should be deterministic and safe for automation assertions.",
    ],
    pitfalls: [
      "Testing only status code and ignoring response body correctness.",
      "Skipping authorization and duplicate request coverage.",
      "Not linking UI failures back to source API inconsistencies.",
    ],
  },
];

const interviewTasks = [
  "Write 10 end-to-end test cases for signup to checkout flow.",
  "Prepare one Playwright suite design for auth, catalog, cart, and order modules.",
  "Identify 5 flaky automation risks in dynamic product grids and suggest fixes.",
  "Write one real JIRA defect for payment success but missing order history entry.",
];

const bugInjectionIdeas = [
  "Product image URL returns broken placeholder only on category page.",
  "Cart total updates visually, but invoice uses stale backend total.",
  "Duplicate email validation appears only after second submit.",
  "Payment button allows double click and creates duplicate orders.",
  "Order history page shows success banner but misses latest order row.",
];

export default function EcommerceAutomationLab() {
  const [activeFlowId, setActiveFlowId] = useState(practiceFlows[0].id);

  const activeFlow = useMemo(
    () => practiceFlows.find((flow) => flow.id === activeFlowId) || practiceFlows[0],
    [activeFlowId]
  );

  return (
    <div className="practice-demo-card automation-scenario-lab">
      <div className="automation-scenario-hero">
        <div>
          <span className="practice-chip">Real-Time Automation Scenario Lab</span>
          <h3>Build an automation practice experience stronger than demo-only websites</h3>
          <p>
            Inspired by common ecommerce automation practice platforms, this lab is designed to feel closer to
            a real QA project: feature coverage, testcase packs, defect thinking, assertions, and problem
            statements in one place.
          </p>
        </div>
        <div className="automation-scenario-scorecard">
          <strong>4</strong>
          <span>high-value suites</span>
          <small>Auth, Catalog, Checkout, API Edge Cases</small>
        </div>
      </div>

      <div className="automation-benchmark-grid">
        {benchmarkFeatures.map((feature) => (
          <article key={feature.title} className="automation-benchmark-card">
            <h4>{feature.title}</h4>
            <p>{feature.detail}</p>
          </article>
        ))}
      </div>

      <div className="automation-flow-tabs">
        {practiceFlows.map((flow) => (
          <button
            type="button"
            key={flow.id}
            className={flow.id === activeFlowId ? "active" : ""}
            onClick={() => setActiveFlowId(flow.id)}
          >
            {flow.label}
          </button>
        ))}
      </div>

      <div className="automation-flow-layout">
        <section className="automation-flow-overview">
          <div className="automation-flow-header">
            <div>
              <span className="practice-section-label">{activeFlow.difficulty}</span>
              <h4>{activeFlow.label}</h4>
            </div>
            <p>{activeFlow.objective}</p>
          </div>

          <div className="automation-problem-box">
            <strong>Problem statement</strong>
            <p>{activeFlow.problemStatement}</p>
          </div>

          <div className="automation-step-map">
            {activeFlow.paths.map((step) => (
              <NavLink key={step.to} to={step.to} className="automation-step-card">
                <strong>{step.label}</strong>
                <span>{step.note}</span>
              </NavLink>
            ))}
          </div>
        </section>

        <section className="automation-flow-details">
          <article className="automation-detail-card">
            <h4>Suggested test cases</h4>
            <ul>
              {activeFlow.testCases.map((testCase) => (
                <li key={testCase}>{testCase}</li>
              ))}
            </ul>
          </article>

          <article className="automation-detail-card">
            <h4>High-value assertions</h4>
            <ul>
              {activeFlow.assertions.map((assertion) => (
                <li key={assertion}>{assertion}</li>
              ))}
            </ul>
          </article>

          <article className="automation-detail-card">
            <h4>Automation pitfalls</h4>
            <ul>
              {activeFlow.pitfalls.map((pitfall) => (
                <li key={pitfall}>{pitfall}</li>
              ))}
            </ul>
          </article>
        </section>
      </div>

      <div className="automation-scenario-grid">
        <article className="automation-side-card">
          <h4>Interview or assignment tasks</h4>
          <ul>
            {interviewTasks.map((task) => (
              <li key={task}>{task}</li>
            ))}
          </ul>
        </article>

        <article className="automation-side-card">
          <h4>Bug injection ideas for practice</h4>
          <ul>
            {bugInjectionIdeas.map((bug) => (
              <li key={bug}>{bug}</li>
            ))}
          </ul>
        </article>
      </div>
    </div>
  );
}
